import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { getApiBase, isAbsoluteUrl, normalizeStoredUrl, resolveAssetUrl } from '../utils/assets';
import {
  getMaxUploadFileSizeMessage,
  getUploadLimitExceededMessage,
  isFileWithinUploadLimit,
  MAX_UPLOAD_FILE_SIZE_MB,
} from '../utils/uploadLimits';

const BUSINESS_DAY_LABELS = [
  { key: 'monday', label: 'Segunda-feira', short: 'Seg' },
  { key: 'tuesday', label: 'Terça-feira', short: 'Ter' },
  { key: 'wednesday', label: 'Quarta-feira', short: 'Qua' },
  { key: 'thursday', label: 'Quinta-feira', short: 'Qui' },
  { key: 'friday', label: 'Sexta-feira', short: 'Sex' },
  { key: 'saturday', label: 'Sábado', short: 'Sáb' },
  { key: 'sunday', label: 'Domingo', short: 'Dom' },
];

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

const createDefaultBusinessHours = () => ({
  timezone: '',
  days: BUSINESS_DAY_LABELS.reduce((acc, { key }, index) => {
    const isWeekend = index >= 5;
    acc[key] = {
      enabled: !isWeekend,
      start: '09:00',
      end: isWeekend ? '13:00' : '18:00',
    };
    return acc;
  }, {}),
});

const cloneBusinessHoursState = (value) => {
  const fallback = createDefaultBusinessHours();
  if (!value || typeof value !== 'object') {
    return fallback;
  }

  const days = BUSINESS_DAY_LABELS.reduce((acc, { key }) => {
    const source = value?.days?.[key];
    if (source && typeof source === 'object') {
      const start = typeof source.start === 'string' && TIME_PATTERN.test(source.start)
        ? source.start
        : fallback.days[key].start;
      const end = typeof source.end === 'string' && TIME_PATTERN.test(source.end)
        ? source.end
        : fallback.days[key].end;
      acc[key] = {
        enabled: Boolean(source.enabled),
        start,
        end,
      };
    } else {
      acc[key] = { ...fallback.days[key] };
    }
    return acc;
  }, {});

  const timezone =
    typeof value.timezone === 'string'
      ? value.timezone
      : typeof value.timezone === 'number'
      ? String(value.timezone)
      : '';

  return {
    timezone,
    days,
  };
};

const prepareBusinessHoursPayload = (value) => {
  const normalized = cloneBusinessHoursState(value);
  const fallback = createDefaultBusinessHours();

  return {
    timezone: normalized.timezone?.trim() ? normalized.timezone.trim() : null,
    days: BUSINESS_DAY_LABELS.reduce((acc, { key }) => {
      const entry = normalized.days[key] || fallback.days[key];
      const start = TIME_PATTERN.test(entry.start) ? entry.start : fallback.days[key].start;
      const end = TIME_PATTERN.test(entry.end) ? entry.end : fallback.days[key].end;
      acc[key] = {
        enabled: Boolean(entry.enabled),
        start,
        end,
      };
      return acc;
    }, {}),
  };
};

const formatBusinessHoursSummary = (value) => {
  const normalized = cloneBusinessHoursState(value);
  const segments = [];
  let current = null;

  BUSINESS_DAY_LABELS.forEach(({ key, short }, index) => {
    const entry = normalized.days[key];
    const label = entry.enabled ? `${entry.start} - ${entry.end}` : 'fechado';
    if (!current) {
      current = { label, start: short, end: short };
    } else if (current.label === label) {
      current.end = short;
    } else {
      segments.push(
        current.start === current.end
          ? `${current.start}: ${current.label}`
          : `${current.start}-${current.end}: ${current.label}`
      );
      current = { label, start: short, end: short };
    }
    if (index === BUSINESS_DAY_LABELS.length - 1 && current) {
      segments.push(
        current.start === current.end
          ? `${current.start}: ${current.label}`
          : `${current.start}-${current.end}: ${current.label}`
      );
    }
  });

  const timezone = normalized.timezone?.trim();
  if (timezone) {
    segments.unshift(`Fuso: ${timezone}`);
  }

  return segments.length ? segments.join(' • ') : '—';
};

const emptyCompany = {
  name: '',
  tradeName: '',
  taxId: '',
  email: '',
  phone: '',
  website: '',
  logoUrl: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  notes: '',
  documents: [],
  businessHours: createDefaultBusinessHours(),
};

const extractFilenameFromDisposition = (header) => {
  if (!header || typeof header !== 'string') {
    return '';
  }

  const utfMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1].trim().replace(/"/g, ''));
    } catch (error) {
      return utfMatch[1].trim().replace(/"/g, '');
    }
  }

  const asciiMatch = header.match(/filename="?([^";]+)"?/i);
  if (asciiMatch?.[1]) {
    return asciiMatch[1].trim();
  }

  return '';
};

const getFilenameFromUrl = (value) => {
  if (!value) {
    return '';
  }

  try {
    const base = getApiBase();
    const parsed = isAbsoluteUrl(value)
      ? new URL(value)
      : base
      ? new URL(value, base.origin)
      : new URL(value, 'http://localhost');

    const nameParam = parsed.searchParams.get('name');
    if (nameParam) {
      return decodeURIComponent(nameParam);
    }

    const segments = parsed.pathname.split('/').filter(Boolean);
    const lastSegment = segments.pop();
    return lastSegment ? decodeURIComponent(lastSegment) : '';
  } catch (error) {
    return '';
  }
};

const normalizeValue = (value) => {
  if (Array.isArray(value)) {
    return JSON.stringify(
      value.map((item) => ({
        id: item?.id ?? null,
        title: item?.title || '',
        fileUrl: item?.fileUrl || '',
        originalName: item?.originalName || '',
      })),
    );
  }
  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return '';
    }
  }
  return value || '';
};

const normalizeTaxId = (value) => (value ? value.replace(/\D/g, '') : '');

const formatTaxId = (value) => {
  const digits = normalizeTaxId(value).slice(0, 14);
  if (!digits) return '';
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

const cloneCompany = (data) => ({
  ...data,
  documents: Array.isArray(data.documents)
    ? data.documents.map((doc) => ({
        id: typeof doc.id === 'number' ? doc.id : undefined,
        title: doc.title || '',
        fileUrl: doc.fileUrl || '',
        localId: doc.localId,
        originalName: doc.originalName || '',
      }))
    : [],
  businessHours: cloneBusinessHoursState(data.businessHours),
});

const createEmptyCompany = () => cloneCompany(emptyCompany);

const generateLocalId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const prepareCompanyState = (data = {}) => {
  const merged = createEmptyCompany();
  Object.keys(emptyCompany).forEach((key) => {
    if (key === 'documents') {
      merged.documents = Array.isArray(data.documents)
        ? data.documents
            .map((doc) => ({
              id: typeof doc.id === 'number' ? doc.id : undefined,
              title: doc.title || '',
              fileUrl: normalizeStoredUrl(
                doc.fileUrl || doc.downloadPath || doc.relativePath || ''
              ),
              originalName:
                doc.originalName || getFilenameFromUrl(doc.fileUrl || doc.downloadPath || ''),
            }))
            .sort((a, b) => {
              if (typeof a.id === 'number' && typeof b.id === 'number') {
                return a.id - b.id;
              }
              if (typeof a.id === 'number') return -1;
              if (typeof b.id === 'number') return 1;
              return 0;
            })
        : [];
    } else if (key === 'taxId') {
      merged.taxId = normalizeTaxId(data.taxId);
    } else if (key === 'logoUrl') {
      merged.logoUrl = normalizeStoredUrl(data.logoUrl);
    } else if (key === 'businessHours') {
      merged.businessHours = cloneBusinessHoursState(data.businessHours);
    } else {
      merged[key] = normalizeValue(data[key]);
    }
  });
  return cloneCompany(merged);
};

export default function CompanyProfile() {
  const [company, setCompany] = useState(() => createEmptyCompany());
  const [initialCompany, setInitialCompany] = useState(() => createEmptyCompany());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [downloadingDocumentKey, setDownloadingDocumentKey] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    api
      .get('/company')
      .then((response) => {
        const data = response.data || {};
        if (!isMounted) return;
        const prepared = prepareCompanyState(data);
        setCompany(prepared);
        setInitialCompany(cloneCompany(prepared));
      })
      .catch(() => {
        if (!isMounted) return;
        const empty = prepareCompanyState();
        setCompany(empty);
        setInitialCompany(cloneCompany(empty));
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setCompany((prev) => {
      const next = cloneCompany(prev);
      next[field] = value;
      return next;
    });
  };

  const handleTaxIdChange = (event) => {
    const digits = normalizeTaxId(event.target.value).slice(0, 14);
    setCompany((prev) => {
      const next = cloneCompany(prev);
      next.taxId = digits;
      return next;
    });
  };

  const handleBusinessHoursTimezoneChange = (event) => {
    const { value } = event.target;
    setCompany((prev) => {
      const next = cloneCompany(prev);
      next.businessHours.timezone = value;
      return next;
    });
  };

  const handleBusinessHoursToggle = (dayKey) => (event) => {
    const { checked } = event.target;
    setCompany((prev) => {
      const next = cloneCompany(prev);
      next.businessHours.days[dayKey] = {
        ...next.businessHours.days[dayKey],
        enabled: checked,
      };
      return next;
    });
  };

  const handleBusinessHoursTimeChange = (dayKey, field) => (event) => {
    const { value } = event.target;
    setCompany((prev) => {
      const next = cloneCompany(prev);
      next.businessHours.days[dayKey] = {
        ...next.businessHours.days[dayKey],
        [field]: value,
      };
      return next;
    });
  };

  const hasChanges = useMemo(() => {
    return Object.keys(emptyCompany).some((key) => {
      const current = normalizeValue(company[key]);
      const initial = normalizeValue(initialCompany[key]);
      return current !== initial;
    });
  }, [company, initialCompany]);

  const resolvedLogoUrl = useMemo(() => resolveAssetUrl(company.logoUrl), [company.logoUrl]);

  const handleLogoUpload = async (event) => {
    const input = event.target;
    const file = input?.files?.[0];
    if (!file) return;

    if (!isFileWithinUploadLimit(file)) {
      alert(getMaxUploadFileSizeMessage());
      if (input) {
        input.value = '';
      }
      return;
    }

    const form = new FormData();
    form.append('file', file);
    setLogoUploading(true);
    try {
      const response = await api.post('/upload', form);
      const rawUrl =
        (response.data?.relativePath && `/uploads/${response.data.relativePath}`) ||
        response.data?.url ||
        '';
      const url = normalizeStoredUrl(rawUrl);
      if (!url) {
        throw new Error('missing url');
      }
      setCompany((prev) => {
        const next = cloneCompany(prev);
        next.logoUrl = url;
        return next;
      });
    } catch (error) {
      const message =
        error?.response?.status === 413
          ? getUploadLimitExceededMessage()
          : error?.response?.data?.error ||
            error?.message ||
            'Falha ao enviar a logo da empresa.';
      alert(message);
    } finally {
      setLogoUploading(false);
      if (input) {
        input.value = '';
      }
    }
  };

  const handleRemoveLogo = () => {
    setCompany((prev) => {
      if (!prev.logoUrl) return prev;
      const next = cloneCompany(prev);
      next.logoUrl = '';
      return next;
    });
  };

  const handleLogoError = () => {
    handleRemoveLogo();
  };

  const handleDocumentUpload = async (event) => {
    const input = event.target;
    const file = input?.files?.[0];
    if (!file) return;

    if (!isFileWithinUploadLimit(file)) {
      alert(getMaxUploadFileSizeMessage());
      if (input) {
        input.value = '';
      }
      return;
    }

    const form = new FormData();
    form.append('file', file);
    setDocumentUploading(true);
    try {
      const response = await api.post('/upload', form);
      const rawUrl =
        response.data?.downloadPath || response.data?.downloadUrl || response.data?.url || '';
      const url = normalizeStoredUrl(rawUrl);
      if (!url) {
        throw new Error('missing url');
      }
      const baseTitle = file.name.replace(/\.[^.]+$/, '').slice(0, 200).trim();
      const title = baseTitle || 'Documento';
      setCompany((prev) => {
        const next = cloneCompany(prev);
        next.documents = [
          ...next.documents,
          {
            id: undefined,
            title,
            fileUrl: url,
            localId: generateLocalId(),
            originalName: file.name,
          },
        ];
        return next;
      });
    } catch (error) {
      const message =
        error?.response?.status === 413
          ? getUploadLimitExceededMessage()
          : error?.response?.data?.error ||
            error?.message ||
            'Falha ao enviar o documento.';
      alert(message);
    } finally {
      setDocumentUploading(false);
      if (input) {
        input.value = '';
      }
    }
  };

  const handleDocumentTitleChange = (index) => (event) => {
    const { value } = event.target;
    setCompany((prev) => {
      const next = cloneCompany(prev);
      next.documents = next.documents.map((doc, docIndex) =>
        docIndex === index ? { ...doc, title: value } : doc
      );
      return next;
    });
  };

  const handleRemoveDocument = (index) => {
    setCompany((prev) => {
      const next = cloneCompany(prev);
      next.documents = next.documents.filter((_, docIndex) => docIndex !== index);
      return next;
    });
  };

  const handleDownloadDocument = async (doc, index) => {
    const downloadKey = doc.id ?? doc.localId ?? index;
    const downloadPath = normalizeStoredUrl(doc.fileUrl);
    if (!downloadPath) {
      alert('Arquivo indisponível para download.');
      return;
    }

    setDownloadingDocumentKey(downloadKey);
    try {
      const response = await api.get(downloadPath, { responseType: 'blob' });
      const filenameFromHeader = extractFilenameFromDisposition(
        response.headers?.['content-disposition']
      );
      const fallbackName =
        filenameFromHeader || doc.originalName || getFilenameFromUrl(downloadPath) || doc.title || 'documento';

      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fallbackName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      alert('Não foi possível baixar o documento.');
    } finally {
      setDownloadingDocumentKey(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!company.name.trim()) {
      alert('Informe o nome da empresa.');
      return;
    }
    if (!hasChanges) return;

    setSaving(true);
    try {
      const { documents, taxId, ...rest } = company;
      const payload = Object.fromEntries(
        Object.entries(rest).map(([key, value]) => [key, value?.trim ? value.trim() : value])
      );
      Object.keys(payload).forEach((key) => {
        if (typeof payload[key] === 'string' && payload[key].trim() === '') {
          payload[key] = null;
        }
      });
      payload.taxId = taxId ? normalizeTaxId(taxId) || null : null;
      payload.logoUrl = normalizeStoredUrl(company.logoUrl) || null;
      payload.documents = documents
        .map((document) => ({
          id: typeof document.id === 'number' ? document.id : undefined,
          title: document.title?.trim() || '',
          fileUrl: normalizeStoredUrl(document.fileUrl?.trim() || ''),
          originalName: document.originalName || '',
        }))
        .filter((document) => document.title && document.fileUrl);

      payload.businessHours = prepareBusinessHoursPayload(company.businessHours);

      const response = await api.put('/company', payload);
      const saved = prepareCompanyState(response.data || {});
      setCompany(saved);
      setInitialCompany(cloneCompany(saved));
      alert('Dados da empresa salvos com sucesso.');
    } catch (error) {
      const message = error.response?.data?.error || 'Não foi possível salvar os dados.';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  const summaryItems = [
    { label: 'Nome fantasia', value: company.tradeName || '—' },
    { label: 'CNPJ/CPF', value: formatTaxId(company.taxId) || '—' },
    { label: 'E-mail', value: company.email || '—' },
    { label: 'Telefone', value: company.phone || '—' },
    { label: 'Site', value: company.website || '—' },
    {
      label: 'Horário de funcionamento',
      value: formatBusinessHoursSummary(company.businessHours),
    },
    {
      label: 'Documentos',
      value: company.documents.length
        ? `${company.documents.length} arquivo${company.documents.length > 1 ? 's' : ''}`
        : '—',
    },
    {
      label: 'Endereço',
      value:
        company.addressLine1 || company.addressLine2 || company.city || company.state || company.zipCode
          ? [
              company.addressLine1,
              company.addressLine2,
              [company.city, company.state].filter(Boolean).join(' - '),
              company.zipCode,
            ]
              .filter((part) => part && part.trim())
              .join(' | ')
          : '—',
    },
  ];

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-semibold mb-2">Dados da empresa</h1>
        <p className="text-gray-600">Carregando informações...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Dados da empresa</h1>
        <p className="text-gray-600">
          Cadastre e atualize as informações institucionais exibidas nos formulários e comunicações.
        </p>
      </div>

      <section className="bg-white shadow rounded-lg p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border bg-gray-50">
              {resolvedLogoUrl ? (
                <img
                  src={resolvedLogoUrl}
                  alt="Logo da empresa"
                  className="h-full w-full object-contain"
                  onError={handleLogoError}
                />
              ) : (
                <span className="text-sm text-gray-400">Sem logo</span>
              )}
            </div>
            <div>
              <p className="text-lg font-medium">Identidade visual</p>
              <p className="text-sm text-gray-500">
                Utilize arquivos PNG ou SVG com fundo transparente para melhor resultado. Tamanho máximo: {MAX_UPLOAD_FILE_SIZE_MB}
                MB.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-start gap-2 md:items-end">
            <label className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 shadow-sm transition hover:bg-blue-100">
              <input type="file" className="hidden" onChange={handleLogoUpload} disabled={logoUploading} />
              <span>{logoUploading ? 'Enviando...' : 'Enviar logo'}</span>
            </label>
            {company.logoUrl && (
              <button
                type="button"
                onClick={handleRemoveLogo}
                className="text-xs text-red-500 hover:underline"
              >
                Remover logo
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-5 bg-white shadow rounded-lg p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              Razão social *
              <input
                type="text"
                value={company.name}
                onChange={handleChange('name')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Nome legal da empresa"
                required
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Nome fantasia
              <input
                type="text"
                value={company.tradeName}
                onChange={handleChange('tradeName')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Como sua empresa é conhecida"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              CNPJ/CPF
              <input
                type="text"
                value={formatTaxId(company.taxId)}
                onChange={handleTaxIdChange}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="00.000.000/0000-00"
                inputMode="numeric"
                maxLength={18}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Site
              <input
                type="text"
                value={company.website}
                onChange={handleChange('website')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="suaempresa.com"
                inputMode="url"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              E-mail de contato
              <input
                type="email"
                value={company.email}
                onChange={handleChange('email')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="contato@suaempresa.com"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Telefone
              <input
                type="tel"
                value={company.phone}
                onChange={handleChange('phone')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="(00) 0000-0000"
              />
            </label>
          </div>

          <div className="space-y-4 rounded-lg border border-gray-200 p-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Horário de funcionamento</p>
              <p className="text-xs text-gray-500">
                Defina os horários utilizados no cálculo de SLA. Dias sem expediente não contarão como atraso.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-medium text-gray-700">
                Fuso horário (opcional)
                <input
                  type="text"
                  value={company.businessHours.timezone}
                  onChange={handleBusinessHoursTimezoneChange}
                  className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Ex: America/Sao_Paulo"
                />
              </label>
            </div>
            <div className="space-y-3">
              {BUSINESS_DAY_LABELS.map(({ key, label }) => {
                const schedule = company.businessHours.days[key];
                return (
                  <div
                    key={key}
                    className="flex flex-col gap-3 rounded-md border border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={schedule.enabled}
                        onChange={handleBusinessHoursToggle(key)}
                      />
                      {label}
                    </label>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                      <span className="hidden sm:inline">Das</span>
                      <input
                        type="time"
                        value={schedule.start}
                        onChange={handleBusinessHoursTimeChange(key, 'start')}
                        disabled={!schedule.enabled}
                        className="rounded border px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed"
                      />
                      <span>às</span>
                      <input
                        type="time"
                        value={schedule.end}
                        onChange={handleBusinessHoursTimeChange(key, 'end')}
                        disabled={!schedule.enabled}
                        className="rounded border px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium text-gray-700 sm:col-span-2">
              Endereço
              <input
                type="text"
                value={company.addressLine1}
                onChange={handleChange('addressLine1')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Rua, avenida, número"
              />
            </label>
            <label className="text-sm font-medium text-gray-700 sm:col-span-2">
              Complemento
              <input
                type="text"
                value={company.addressLine2}
                onChange={handleChange('addressLine2')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Sala, andar, bairro"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Cidade
              <input
                type="text"
                value={company.city}
                onChange={handleChange('city')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Estado
              <input
                type="text"
                value={company.state}
                onChange={handleChange('state')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              CEP
              <input
                type="text"
                value={company.zipCode}
                onChange={handleChange('zipCode')}
                className="mt-1 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="00000-000"
              />
            </label>
          </div>

          <label className="block text-sm font-medium text-gray-700">
            Observações
            <textarea
              value={company.notes}
              onChange={handleChange('notes')}
              className="mt-1 h-28 w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Informações adicionais sobre a empresa"
            />
          </label>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setCompany(cloneCompany(initialCompany))}
              disabled={!hasChanges || saving}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Descartar alterações
            </button>
            <button
              type="submit"
              disabled={!hasChanges || saving}
              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
            >
              {saving ? 'Salvando...' : 'Salvar dados'}
            </button>
          </div>
        </form>

        <aside className="space-y-4 rounded-lg bg-white p-5 shadow">
          <h2 className="text-lg font-semibold text-gray-800">Resumo rápido</h2>
          <dl className="space-y-3">
            {summaryItems.map((item) => (
              <div key={item.label} className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {item.label}
                </dt>
                <dd className="text-sm text-gray-700 break-words">{item.value}</dd>
              </div>
            ))}
          </dl>
          {company.notes && (
            <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-700">
              <p className="font-medium">Observações</p>
              <p>{company.notes}</p>
            </div>
          )}
        </aside>
      </section>

      <section className="bg-white shadow rounded-lg p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Documentos da empresa</h2>
            <p className="text-sm text-gray-500">
              Armazene arquivos institucionais e mantenha um histórico organizado.
            </p>
          </div>
          <label
            className={`inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 shadow-sm transition ${
              documentUploading ? 'cursor-not-allowed opacity-60 pointer-events-none' : 'hover:bg-blue-100'
            }`}
            aria-disabled={documentUploading}
          >
            <input
              type="file"
              className="hidden"
              onChange={handleDocumentUpload}
              disabled={documentUploading}
            />
            <span>{documentUploading ? 'Enviando...' : 'Adicionar documento'}</span>
          </label>
          <p className="text-xs text-gray-500">
            Tamanho máximo do arquivo: {MAX_UPLOAD_FILE_SIZE_MB} MB.
          </p>
        </div>

        <div className="mt-4 space-y-3">
          {company.documents.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nenhum documento cadastrado até o momento. Utilize o botão acima para enviar arquivos.
            </p>
          ) : (
            <ul className="space-y-3">
              {company.documents.map((doc, index) => {
                const docKey = doc.id ?? doc.localId ?? index;
                const isDownloading = downloadingDocumentKey === docKey;
                return (
                  <li
                    key={docKey}
                    className="rounded-lg border border-gray-200 p-4"
                  >
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="flex-1 space-y-2">
                        <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                          Título do documento
                        </label>
                        <input
                          type="text"
                          value={doc.title}
                          onChange={handleDocumentTitleChange(index)}
                          className="w-full rounded border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Nome do arquivo"
                          maxLength={200}
                        />
                        <button
                          type="button"
                          onClick={() => handleDownloadDocument(doc, index)}
                          disabled={isDownloading}
                          className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 shadow-sm transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isDownloading ? 'Baixando...' : 'Baixar documento'}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveDocument(index)}
                        className="self-start rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50"
                      >
                        Remover
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
